import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCart } from '../../Context/Cartcontext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './OfferView.css';
import { getImageUrl } from '../../utils/imageUtils';

const OfferView = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isOfferActive, setIsOfferActive] = useState(true);
  
  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`${baseURL}/api/products/getproduct/${id}`);
        const data = await response.json();

        if (!response.ok || !data.success || !data.product) {
          throw new Error(data.message || 'Failed to fetch product');
        }

        if (data.product.type !== 'special_offer') {
          throw new Error('This product is not a special offer');
        }

        setProduct(data.product);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (!product || !product.validUntil) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const end = new Date(product.validUntil);
      const diff = end - now;

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / 1000 / 60) % 60);
        const seconds = Math.floor((diff / 1000) % 60);
        setTimeLeft({ days, hours, minutes, seconds });
        setIsOfferActive(true);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsOfferActive(false);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [product]);

  const handleAddToCart = () => {
    if (product && isOfferActive) {
      const cartItem = {
        id: product._id,
        name: product.name,
        price: product.offerPrice || product.regularPrice,
        regularPrice: product.regularPrice,
        offerPrice: product.offerPrice,
        imageUrl: product.imageUrl,
        stock: product.stock,
        type: 'special_offer'
      };

      addToCart(cartItem);
      setAddedToCart(true);
      toast.success(`${product.name} added to cart!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });

      setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  if (loading) {
    return (
      <>
        <div className="ov-loading-container">
          <div className="ov-loading-spinner"></div>
        </div>
        <ToastContainer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="ov-error-container">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
        <ToastContainer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <div className="ov-not-found-container">
          <h3>Offer not found</h3>
          <p>The requested offer could not be found.</p>
        </div>
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      <div className="ov-offer-view">
        <div className="ov-offer-image">
          <img
            src={getImageUrl(product.imageUrl)}
            alt={product.name}
          />
        </div>
        <div className="ov-offer-details">
          <h1>{product.name}</h1>
          <div className="ov-offer-badge">
              <span>% Special Offer</span>
          </div>

          <div className="ov-offer-features">
            <h3>Key Features</h3>
            <ul>
              {Array.isArray(product.description)
                ? product.description.map((item, idx) => <li key={idx}>{item}</li>)
                : <li>{product.description}</li>}
            </ul>
          </div>

          <div className="ov-countdown">
            <h4>{isOfferActive ? 'Limited Time Offer' : 'Offer Expired'}</h4>
            {isOfferActive ? (
              <div className="ov-countdown-timer">
                <div className="ov-time-unit">
                  <div className="ov-value">{timeLeft.days}</div>
                  <div className="ov-label">Days</div>
                </div>
                <div className="ov-time-unit">
                  <div className="ov-value">{timeLeft.hours}</div>
                  <div className="ov-label">Hours</div>
                </div>
                <div className="ov-time-unit">
                  <div className="ov-value">{timeLeft.minutes}</div>
                  <div className="ov-label">Minutes</div>
                </div>
                <div className="ov-time-unit">
                  <div className="ov-value">{timeLeft.seconds}</div>
                  <div className="ov-label">Seconds</div>
                </div>
              </div>
            ) : (
              <div className="ov-offer-expired">
                <p>This offer has timed out and is no longer available.</p>
              </div>
            )}
          </div>

          <div className="ov-pricing">
            <h3>Regular Price: LKR {product.regularPrice?.toLocaleString()}</h3>
            {isOfferActive ? (
              <>
                <h3>Offer Price: LKR {product.offerPrice?.toLocaleString()}</h3>
                <h3>Discount: {product.discount}% OFF</h3>
              </>
            ) : (
              <h3 className="ov-expired-price">Offer Price: LKR {product.offerPrice?.toLocaleString()} (Expired)</h3>
            )}
          </div>

          <div className="ov-validity">
            <p><span>Valid From:</span> {new Date(product.validFrom).toLocaleDateString()}</p>
            <p><span>Valid Until:</span> {new Date(product.validUntil).toLocaleDateString()}</p>
          </div>

          <div className="ov-stock-status">
            <span className={product.stock > 0 ? "ov-in-stock" : "ov-out-of-stock"}>
              {product.stock > 0 ? "In Stock" : "Out of Stock"}
            </span>
          </div>

          {isOfferActive && product.stock > 0 && (
            <button 
              className={`ov-add-to-cart-btn ${addedToCart ? 'ov-added' : ''}`}
              onClick={handleAddToCart}
              disabled={addedToCart}
            >
              {addedToCart ? 'Added to Cart ✓' : 'Add to Cart'}
            </button>
          )}

          {!isOfferActive && (
            <div className="ov-offer-timeout-message">
              <p>⏰ This offer has timed out and is no longer available for purchase.</p>
            </div>
          )}

          {addedToCart && (
            <div className="ov-cart-success-message">
              <p>✓ Product added to cart successfully!</p>
            </div>
          )}
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default OfferView;